import { useEffect, useMemo, useState, type FormEvent } from "react";
import type {
  ConversationAccess,
  ConversationMemberRead,
  ConversationRead,
  ConversationUpdate,
  DirectoryUserRead,
  RetrievalProfileRead,
  UUID,
} from "../../shared/api/types";
import { Badge, Button, Callout, Field, Input, Modal, Select, Spinner } from "../../shared/ui/Primitives";
import { useToast } from "../../shared/ui/Toast";
import { responseModeOptions, visibilityOptions } from "./constants";

export function ConversationSettings({
  open,
  conversation,
  profiles,
  users,
  members,
  membersLoading,
  canShare,
  canTransfer,
  onClose,
  onSave,
  onShare,
  onRemoveMember,
  onTransfer,
}: {
  open: boolean;
  conversation: ConversationRead;
  profiles: RetrievalProfileRead[];
  users: DirectoryUserRead[];
  members: ConversationMemberRead[];
  membersLoading: boolean;
  canShare: boolean;
  canTransfer: boolean;
  onClose: () => void;
  onSave: (payload: ConversationUpdate) => Promise<void>;
  onShare: (userId: UUID, access: ConversationAccess) => Promise<void>;
  onRemoveMember: (userId: UUID) => Promise<void>;
  onTransfer: (userId: UUID) => Promise<void>;
}) {
  const { showToast } = useToast();
  const [title, setTitle] = useState(conversation.title);
  const [visibility, setVisibility] = useState(conversation.visibility);
  const [responseMode, setResponseMode] = useState(conversation.response_mode);
  const [profileId, setProfileId] = useState(conversation.retrieval_profile_id ?? "");
  const [departmentId, setDepartmentId] = useState(conversation.department_id?.toString() ?? "");
  const [saving, setSaving] = useState(false);
  const [shareUserId, setShareUserId] = useState("");
  const [shareAccess, setShareAccess] = useState<ConversationAccess>("read");
  const [transferUserId, setTransferUserId] = useState("");
  const [memberActionId, setMemberActionId] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    setTitle(conversation.title);
    setVisibility(conversation.visibility);
    setResponseMode(conversation.response_mode);
    setProfileId(conversation.retrieval_profile_id ?? "");
    setDepartmentId(conversation.department_id?.toString() ?? "");
  }, [conversation]);

  const directoryUsers = useMemo(
    () => users.filter((item) => item.id !== conversation.owner_id),
    [conversation.owner_id, users],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        visibility,
        response_mode: responseMode,
        retrieval_profile_id: profileId || null,
        department_id: visibility === "department" && departmentId ? Number(departmentId) : null,
      });
      onClose();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось сохранить настройки беседы", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function addOrUpdateMember() {
    if (!shareUserId) return;
    setSharing(true);
    try {
      await onShare(shareUserId, shareAccess);
      setShareUserId("");
      setShareAccess("read");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось сохранить доступ", "danger");
    } finally {
      setSharing(false);
    }
  }

  async function changeMemberAccess(userId: UUID, access: ConversationAccess) {
    setMemberActionId(userId);
    try {
      await onShare(userId, access);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось изменить доступ", "danger");
    } finally {
      setMemberActionId(null);
    }
  }

  async function removeMember(userId: UUID) {
    setMemberActionId(userId);
    try {
      await onRemoveMember(userId);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось удалить участника", "danger");
    } finally {
      setMemberActionId(null);
    }
  }

  async function transfer() {
    if (!transferUserId) return;
    setTransferring(true);
    try {
      await onTransfer(transferUserId);
      setTransferUserId("");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось передать беседу", "danger");
    } finally {
      setTransferring(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Настройки беседы"
      onClose={onClose}
      wide
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button form="conversation-settings-form" type="submit" disabled={saving || !title.trim()}>
            {saving ? "Сохраняем…" : "Сохранить"}
          </Button>
        </>
      )}
    >
      <form id="conversation-settings-form" className="form-grid" onSubmit={submit}>
        <Field label="Название" className="form-grid__wide">
          <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={500} />
        </Field>
        <Field label="Режим ответа">
          <Select value={responseMode} onChange={(event) => setResponseMode(event.target.value as typeof responseMode)}>
            {responseModeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </Field>
        <Field label="Видимость">
          <Select value={visibility} onChange={(event) => setVisibility(event.target.value as typeof visibility)}>
            {visibilityOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </Field>
        {visibility === "department" ? (
          <Field label="ID подразделения">
            <Input type="number" min={1} value={departmentId} onChange={(event) => setDepartmentId(event.target.value)} />
          </Field>
        ) : null}
        <Field label="Профиль поиска" hint={profiles.length ? undefined : "Используется основной профиль сервера."}>
          <Select value={profileId} onChange={(event) => setProfileId(event.target.value)} disabled={!profiles.length}>
            <option value="">Основной профиль</option>
            {profiles.filter((item) => item.is_active).map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}{profile.is_default ? " — по умолчанию" : ""}
              </option>
            ))}
          </Select>
        </Field>
      </form>

      {(canShare || canTransfer) ? (
        <div className="settings-sections">
          <section>
            <div className="section-title-row">
              <div><h3>Участники беседы</h3><p>Владелец имеет полный доступ. Остальным можно разрешить чтение или продолжение диалога.</p></div>
              {membersLoading ? <Spinner label="Загрузка участников" /> : <Badge tone="info">{members.length}</Badge>}
            </div>
            {!membersLoading && members.length ? (
              <div className="conversation-member-list">
                {members.map((member) => (
                  <div className="conversation-member" key={member.user_id}>
                    <span className="user-avatar">{(member.user.first_name[0] || member.user.last_name[0] || "П").toUpperCase()}</span>
                    <div className="conversation-member__identity">
                      <strong>{member.user.display_name}</strong>
                      <span>{member.user.work_position || `Подразделения: ${member.user.department_ids.join(", ") || "—"}`}</span>
                    </div>
                    {member.is_owner ? (
                      <Badge tone="success">Владелец</Badge>
                    ) : canShare ? (
                      <Select
                        aria-label={`Доступ: ${member.user.display_name}`}
                        value={member.access}
                        disabled={memberActionId === member.user_id}
                        onChange={(event) => void changeMemberAccess(member.user_id, event.target.value as ConversationAccess)}
                      >
                        <option value="read">Только чтение</option>
                        <option value="write">Можно продолжать</option>
                      </Select>
                    ) : (
                      <Badge>{member.access === "write" ? "Можно продолжать" : "Только чтение"}</Badge>
                    )}
                    {!member.is_owner && canShare ? (
                      <Button
                        size="small"
                        variant="ghost"
                        disabled={memberActionId === member.user_id}
                        onClick={() => void removeMember(member.user_id)}
                      >
                        {memberActionId === member.user_id ? "Обновляем…" : "Удалить"}
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
            {!membersLoading && !members.length ? <Callout tone="warning">Список участников не получен.</Callout> : null}
          </section>

          {canShare ? (
            <section>
              <h3>Добавить сотрудника или изменить доступ</h3>
              {directoryUsers.length ? (
                <div className="inline-form">
                  <Select aria-label="Сотрудник" value={shareUserId} onChange={(event) => setShareUserId(event.target.value)}>
                    <option value="">Выберите сотрудника</option>
                    {directoryUsers.map((item) => (
                      <option key={item.id} value={item.id}>{item.display_name}{item.work_position ? ` — ${item.work_position}` : ""}</option>
                    ))}
                  </Select>
                  <Select aria-label="Уровень доступа" value={shareAccess} onChange={(event) => setShareAccess(event.target.value as ConversationAccess)}>
                    <option value="read">Только чтение</option>
                    <option value="write">Можно продолжать беседу</option>
                  </Select>
                  <Button variant="secondary" disabled={!shareUserId || sharing} onClick={() => void addOrUpdateMember()}>
                    {sharing ? "Сохраняем…" : "Добавить / обновить"}
                  </Button>
                </div>
              ) : (
                <Callout tone="info">Каталог пока пуст. Сотрудник появится после первого входа в приложение через Bitrix24.</Callout>
              )}
            </section>
          ) : null}

          {canTransfer ? (
            <section>
              <h3>Передача владельца</h3>
              {directoryUsers.length ? (
                <div className="inline-form">
                  <Select aria-label="Новый владелец" value={transferUserId} onChange={(event) => setTransferUserId(event.target.value)}>
                    <option value="">Выберите нового владельца</option>
                    {directoryUsers.map((item) => (
                      <option key={item.id} value={item.id}>{item.display_name}{item.work_position ? ` — ${item.work_position}` : ""}</option>
                    ))}
                  </Select>
                  <Button variant="secondary" disabled={!transferUserId || transferring} onClick={() => void transfer()}>
                    {transferring ? "Передаём…" : "Передать"}
                  </Button>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
